/**
 * The sustain pedal, for notes played live.
 *
 * A note released while the pedal is down keeps sounding until the pedal
 * lifts. That is a statement about *releases*, not about the instrument, so it
 * lives between the controller and the instrument rather than inside either:
 * the pedal decides whether a note-off is passed on now or kept until the
 * pedal comes up. Pure bookkeeping, with no audio in it, so it can be tested
 * without an audio context — which the live path otherwise needs.
 *
 * A pitch struck again while it is being held is released by the pedal first:
 * a real damper lifts for the restrike, and an instrument handed two notes at
 * one pitch cannot tell which release belongs to which.
 */
export class SustainPedal {
  private down = false;
  private readonly held = new Set<number>();

  get isDown(): boolean {
    return this.down;
  }

  /** Pitches whose release the pedal is holding, in the order they were released. */
  get holding(): number[] {
    return [...this.held];
  }

  /**
   * A key going down. Returns true when this pitch was being held by the
   * pedal, in which case the caller should release it before striking it.
   */
  noteOn(pitch: number): boolean {
    return this.held.delete(pitch);
  }

  /** A key coming up. Returns true when the release should reach the instrument now. */
  noteOff(pitch: number): boolean {
    if (!this.down) return true;
    this.held.add(pitch);
    return false;
  }

  /** The pedal moving. Returns the pitches to release now — none unless it lifted. */
  set(down: boolean): number[] {
    this.down = down;
    if (down) return [];
    const released = [...this.held];
    this.held.clear();
    return released;
  }

  /** Everything let go, pedal and all: all-notes-off, or the transport stopping. */
  clear(): number[] {
    this.down = false;
    const released = [...this.held];
    this.held.clear();
    return released;
  }
}
